import React, { useRef, useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader.js';
import './ThreeHeartBackground.css';

/**
 * ThreeHeartBackground 组件
 * 
 * 功能：
 * - 使用 Three.js 创建 3D 心形粒子系统
 * - 基于心形数学方程生成粒子：(x² + 9/4 * z² + y² - 1)³ - x² * y³ - 9/80 * z² * y³ < 0
 * - 整体心跳动画效果（整个心形同步缩放）
 * - 鼠标移动交互：心形跟随鼠标左右倾斜
 * - 手机陀螺仪交互：心形跟随设备倾斜
 * - 自动旋转摆动：长时间无操作时自动旋转
 * - 后处理效果：UnrealBloomPass 发光 + FXAA 抗锯齿
 * 
 * 优化：
 * - 使用 InstancedMesh 优化大量粒子渲染
 * - 使用对象键值对去重算法减少计算复杂度
 * - 使用 ref 存储心率值，避免 useEffect 重复初始化场景
 */

interface ThreeHeartBackgroundProps {
  heartRate: number; // 心跳速率 (0-200 BPM)
}

const ThreeHeartBackground: React.FC<ThreeHeartBackgroundProps> = ({ heartRate }) => {
  // Refs for Three.js objects
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const heartMeshRef = useRef<THREE.InstancedMesh | null>(null);
  const composerRef = useRef<EffectComposer | null>(null);
  const finalComposerRef = useRef<EffectComposer | null>(null);
  const materialsRef = useRef<Record<string, THREE.Material | THREE.Color>>({});
  const resultRef = useRef<Array<{x: number, y: number, z: number}>>([]);
  const animationFrameRef = useRef<number>();
  const pointLightRef = useRef<THREE.PointLight | null>(null);
  const heartGroupRef = useRef<THREE.Group | null>(null);
  
  // 交互相关 refs
  const mouseXRef = useRef(0);
  const targetRotationRef = useRef(0);
  const currentRotationRef = useRef(0);
  const lastActivityTimeRef = useRef(Date.now());
  const autoRotateAngleRef = useRef(0);
  
  // 使用 ref 存储心率，避免触发 useEffect 重新初始化场景
  const heartRateRef = useRef(heartRate);

  // 更新心率 ref 值
  useEffect(() => {
    heartRateRef.current = heartRate;
  }, [heartRate]);

  // Bloom layer for post-processing
  const bloomLayer = useMemo(() => {
    const layer = new THREE.Layers();
    layer.set(1);
    return layer;
  }, []);

  /**
   * 生成心形粒子点
   * 使用心形方程和去重算法生成表面点
   */
  const generateHeartPoints = useMemo(() => {
    const points: Array<{x: number, y: number, z: number}> = [];
    let arr_xy: Array<{x: number, y: number, max_z: number, min_z: number}> = [];
    let arr_yz: Array<{z: number, y: number, max_x: number, min_x: number}> = [];
    let arr_xz: Array<{x: number, z: number, max_y: number, min_y: number}> = [];

    const unitSpacing = 0.04; // 粒子间距

    // 使用对象键值对去重
    let kvx: Record<string, number[]> = {};
    let kvy: Record<string, number[]> = {};
    let kvz: Record<string, number[]> = {};

    // 遍历 3D 空间生成心形点
    for (let x = -1.12; x <= 1.12; x += unitSpacing) {
      for (let y = -0.96; y <= 1.2; y += unitSpacing) {
        for (let z = -0.64; z <= 0.64; z += unitSpacing) {
          const xf = Number(x.toFixed(2));
          const yf = Number(y.toFixed(2));
          const zf = Number(z.toFixed(2));

          // 心形方程
          const func = Math.pow(Math.pow(xf, 2) + 9 / 4 * Math.pow(zf, 2) + Math.pow(yf, 2) - 1, 3) - 
                     Math.pow(xf, 2) * Math.pow(yf, 3) - 
                     9 / 80 * Math.pow(zf, 2) * Math.pow(yf, 3);

          if (func < 0) {
            points.push({ x: xf, y: yf, z: zf });

            if (!kvx[`${yf}_${zf}`]) kvx[`${yf}_${zf}`] = [];
            kvx[`${yf}_${zf}`].push(xf);

            if (!kvy[`${xf}_${zf}`]) kvy[`${xf}_${zf}`] = [];
            kvy[`${xf}_${zf}`].push(yf);

            if (!kvz[`${xf}_${yf}`]) kvz[`${xf}_${yf}`] = [];
            kvz[`${xf}_${yf}`].push(zf);

            arr_xy.push({ x: xf, y: yf, max_z: 0, min_z: 0 });
            arr_yz.push({ z: zf, y: yf, max_x: 0, min_x: 0 });
            arr_xz.push({ x: xf, z: zf, max_y: 0, min_y: 0 });
          }
        }
      }
    }

    // 去重函数
    const objArrDistinct = (objArr: Array<any>) => {
      const resultArr: Array<any> = [];
      const itemKeyVal: Record<string, boolean> = {};
      objArr.forEach((item) => {
        if (!itemKeyVal[`${item.x}_${item.y}_${item.z}`]) {
          itemKeyVal[`${item.x}_${item.y}_${item.z}`] = true;
          resultArr.push(item);
        }
      });
      return resultArr;
    };

    arr_xy = objArrDistinct(arr_xy);
    arr_yz = objArrDistinct(arr_yz);
    arr_xz = objArrDistinct(arr_xz);

    // 计算最大最小值
    arr_xy.forEach((xy) => {
      xy.min_z = Math.min(...kvz[`${xy.x}_${xy.y}`]);
      xy.max_z = Math.max(...kvz[`${xy.x}_${xy.y}`]);
    });

    arr_yz.forEach((yz) => {
      yz.min_x = Math.min(...kvx[`${yz.y}_${yz.z}`]);
      yz.max_x = Math.max(...kvx[`${yz.y}_${yz.z}`]);
    });

    arr_xz.forEach((xz) => {
      xz.min_y = Math.min(...kvy[`${xz.x}_${xz.z}`]);
      xz.max_y = Math.max(...kvy[`${xz.x}_${xz.z}`]);
    });

    // 提取表面点
    const result: Array<{x: number, y: number, z: number}> = [];

    arr_xy.forEach((xy) => {
      result.push({ x: xy.x, y: xy.y, z: xy.max_z });
      result.push({ x: xy.x, y: xy.y, z: xy.min_z });
    });

    arr_yz.forEach((yz) => {
      result.push({ z: yz.z, y: yz.y, x: yz.max_x });
      result.push({ z: yz.z, y: yz.y, x: yz.min_x });
    });

    arr_xz.forEach((xz) => {
      result.push({ x: xz.x, z: xz.z, y: xz.max_y });
      result.push({ x: xz.x, z: xz.z, y: xz.min_y });
    });

    return objArrDistinct(result);
  }, []);

  // 随机打乱粒子顺序
  resultRef.current = useMemo(() => {
    const sorted = [...generateHeartPoints].sort(() => Math.random() > 0.5 ? -1 : 1);
    return sorted;
  }, [generateHeartPoints]);

  // 初始化 Three.js 场景
  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;

    // 创建渲染器
    const renderer = new THREE.WebGLRenderer({ antialias: false, alpha: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(container.clientWidth, container.clientHeight);
    rendererRef.current = renderer;
    container.appendChild(renderer.domElement);

    // 创建场景
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x111111);
    sceneRef.current = scene;

    // 创建相机
    const camera = new THREE.PerspectiveCamera(
      45,
      container.clientWidth / container.clientHeight,
      1,
      10000
    );
    camera.position.set(0, 0, 8); // 正视图：相机在 z 轴正方向
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    // 创建点光源
    const pointLight = new THREE.PointLight(0xffffff);
    scene.add(pointLight);
    pointLightRef.current = pointLight;

    // 创建心形网格
    const unitSize = 0.01;
    const geometry = new THREE.BoxGeometry(unitSize, unitSize, unitSize);
    const material = new THREE.MeshBasicMaterial();

    const heartMesh = new THREE.InstancedMesh(
      geometry,
      material,
      resultRef.current.length
    );
    heartMesh.name = 'Heart';
    heartMeshRef.current = heartMesh;

    // 设置每个粒子的位置和颜色
    resultRef.current.forEach((res, i) => {
      const dummy = new THREE.Object3D();
      dummy.position.set(res.x, res.y, res.z);
      dummy.updateMatrix();
      heartMesh.setMatrixAt(i, dummy.matrix);
      heartMesh.setColorAt(i, new THREE.Color(`rgb(${Math.floor(255 * Math.random())}, 0, 0)`));
    });

    heartMesh.layers.enable(1);

    // 将心形添加到组中，用于整体缩放和旋转动画
    const heartGroup = new THREE.Group();
    heartGroup.add(heartMesh);
    scene.add(heartGroup);
    heartGroupRef.current = heartGroup;
    
    // 设置初始旋转角度为 0（正视图）
    heartGroup.rotation.y = 0;

    // 后处理：暗化非发光对象
    const darkMaterial = new THREE.MeshBasicMaterial({ color: 0x000000 });

    const darkenNonBloomed = (obj: THREE.Object3D) => {
      if (obj instanceof THREE.Scene) {
        if (!materialsRef.current['scene']) {
          materialsRef.current['scene'] = obj.background as THREE.Color;
        }
        obj.background = null;
        return;
      }
      if (obj instanceof THREE.Sprite || ((obj as THREE.Mesh).isMesh && !bloomLayer.test(obj.layers))) {
        if (!materialsRef.current[obj.uuid]) {
          materialsRef.current[obj.uuid] = (obj as THREE.Mesh).material as THREE.Material;
        }
        (obj as THREE.Mesh).material = darkMaterial;
      }
    };

    const restoreMaterial = (obj: THREE.Object3D) => {
      if (obj instanceof THREE.Scene) {
        if (materialsRef.current['scene'] && materialsRef.current['scene'] instanceof THREE.Color) {
          obj.background = materialsRef.current['scene'] as THREE.Color;
        }
        delete materialsRef.current['scene'];
        return;
      }
      if (materialsRef.current[obj.uuid]) {
        (obj as THREE.Mesh).material = materialsRef.current[obj.uuid] as THREE.Material;
        delete materialsRef.current[obj.uuid];
      }
    };

    // 后处理渲染管线
    const renderScene = new RenderPass(scene, camera);

    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(container.clientWidth, container.clientHeight),
      1.5,
      0.4,
      0.85
    );
    bloomPass.threshold = 0;
    bloomPass.strength = 1.5;
    bloomPass.radius = 0;

    const bloomComposer = new EffectComposer(renderer);
    bloomComposer.renderToScreen = false;
    bloomComposer.addPass(renderScene);
    bloomComposer.addPass(bloomPass);

    const effectFXAA = new ShaderPass(FXAAShader);
    effectFXAA.uniforms['resolution'].value.set(
      0.6 / container.clientWidth,
      0.6 / container.clientHeight
    );
    effectFXAA.renderToScreen = true;

    const vertexShader = `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `;

    const fragmentShader = `
      uniform sampler2D baseTexture;
      uniform sampler2D bloomTexture;
      varying vec2 vUv;
      void main() {
        gl_FragColor = (texture2D(baseTexture, vUv) + vec4(1.0) * texture2D(bloomTexture, vUv));
      }
    `;

    const finalPass = new ShaderPass(
      new THREE.ShaderMaterial({
        uniforms: {
          baseTexture: { value: null },
          bloomTexture: { value: bloomComposer.renderTarget2.texture },
        },
        vertexShader,
        fragmentShader,
        defines: {},
      }),
      'baseTexture'
    );
    finalPass.needsSwap = true;

    const finalComposer = new EffectComposer(renderer);
    finalComposer.addPass(renderScene);
    finalComposer.addPass(finalPass);
    finalComposer.addPass(effectFXAA);

    composerRef.current = bloomComposer;
    finalComposerRef.current = finalComposer;

    // 鼠标移动处理
    const handleMouseMove = (e: MouseEvent) => {
      const centerX = window.innerWidth / 2;
      const normalizedX = (e.clientX - centerX) / centerX; // -1 to 1
      mouseXRef.current = normalizedX;
      targetRotationRef.current = normalizedX * 0.3; // 最大旋转角度 0.3 弧度
      lastActivityTimeRef.current = Date.now();
    };

    // 触摸移动处理
    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        const centerX = window.innerWidth / 2;
        const normalizedX = (e.touches[0].clientX - centerX) / centerX;
        mouseXRef.current = normalizedX;
        targetRotationRef.current = normalizedX * 0.3;
        lastActivityTimeRef.current = Date.now();
      }
    };

    // 陀螺仪处理
    const handleOrientation = (e: DeviceOrientationEvent) => {
      if (e.gamma !== null) {
        // gamma 是左右倾斜角度 (-90 到 90)
        const normalizedGamma = e.gamma / 90; // -1 to 1
        targetRotationRef.current = normalizedGamma * 0.3;
        lastActivityTimeRef.current = Date.now();
      }
    };

    // 添加事件监听器
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('deviceorientation', handleOrientation);

    // 动画循环
    const animate = () => {
      const time = Date.now() * 0.001;
      const heartSpeed = heartRateRef.current / 60;

      // 检查是否长时间无操作（10秒）
      const timeSinceLastActivity = Date.now() - lastActivityTimeRef.current;
      const isAutoRotate = timeSinceLastActivity > 10000;

      if (isAutoRotate) {
        // 自动旋转摆动
        autoRotateAngleRef.current = Math.sin(time * 0.5) * 0.15; // 0.15 弧度的摆动
        targetRotationRef.current = autoRotateAngleRef.current;
      }

      // 平滑过渡到目标旋转角度
      currentRotationRef.current += (targetRotationRef.current - currentRotationRef.current) * 0.05;

      // 应用旋转
      if (heartGroupRef.current) {
        heartGroupRef.current.rotation.y = currentRotationRef.current;
      }

      // 整体心跳动画：对整个组进行缩放
      const scale = 1.0 + 0.1 * (0.5 * Math.sin(time * heartSpeed * 2) + 0.5);
      if (heartMeshRef.current && heartMeshRef.current.parent) {
        heartMeshRef.current.parent.scale.set(scale, scale, scale);
      }

      // 渲染
      scene.traverse(darkenNonBloomed);
      bloomComposer.render();
      scene.traverse(restoreMaterial);
      finalComposer.render();

      // 更新光源位置
      if (cameraRef.current && pointLightRef.current) {
        const vector = cameraRef.current.position.clone();
        pointLightRef.current.position.set(vector.x, vector.y, vector.z);
      }

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    // 窗口大小调整处理
    const handleResize = () => {
      if (!cameraRef.current || !rendererRef.current || !composerRef.current || !finalComposerRef.current) return;

      cameraRef.current.aspect = container.clientWidth / container.clientHeight;
      cameraRef.current.updateProjectionMatrix();

      rendererRef.current.setSize(container.clientWidth, container.clientHeight);
      composerRef.current.setSize(container.clientWidth, container.clientHeight);
      finalComposerRef.current.setSize(container.clientWidth, container.clientHeight);

      effectFXAA.uniforms['resolution'].value.set(
        0.6 / container.clientWidth,
        0.6 / container.clientHeight
      );
    };

    window.addEventListener('resize', handleResize);

    // 清理函数
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('deviceorientation', handleOrientation);
      window.removeEventListener('resize', handleResize);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (container && renderer.domElement) {
        container.removeChild(renderer.domElement);
      }
      renderer.dispose();
      geometry.dispose();
      material.dispose();
      heartMesh.dispose();
      bloomComposer.dispose();
      finalComposer.dispose();
    };
  }, [bloomLayer]);

  return <div ref={containerRef} className="three-heart-background" />;
};

export default ThreeHeartBackground;